import { useMemo } from 'react'
import { useQuery, UseQueryResult } from 'react-query'

import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import { useYogiQLFilterContext } from 'features/filters/hooks/use-yogiql-filter-context'
import { useFilterService } from 'features/filters/service'
import { fetchPanelFilterOptions } from 'features/filters/service/fetch-yogiql-filter-options'
import type { FilterOption } from 'features/filters/types'

/**
 * Per-panel fetcher for `/post/filter/options/v2`. Includes the panel's
 * filter-affecting state (filters, dateRange, searchTerms, searchCriteria)
 * in the query key so React Query refetches automatically as the user
 * adjusts the panel. Closes PROD-2077.
 *
 * Caller passes the currently-expanded field in ``options.fields``; an
 * empty / undefined ``fields`` gates the fetch off (the popover's "All"
 * view shows names-only Skeletons via the stripped fallback). Per
 * V1 parity (PROD-3136), counts are computed lazily — one BE round-trip
 * per field expansion — instead of eagerly for every field on every
 * panel-state change.
 *
 * Deliberately does NOT set `staleTime: Infinity` — the whole point is the
 * query refetches when the key changes.
 *
 * Deliberately does NOT set `keepPreviousData: true`. When the panel state
 * changes the query key changes and `data` becomes `undefined` until the
 * fresh fetch lands. Consumers (`useEffectiveFilterList`,
 * `ChartControlsSidebar`) fall back to the names-only
 * `stripFilterCounts(...)` copy in that gap, which the popover renders
 * as a Skeleton placeholder for each count cell. The UX intent is to
 * never show a count that doesn't match the current filter selection
 * (PROD-3130).
 *
 * Forwards React Query's `QueryFunctionContext.signal` into the fetcher
 * so the in-flight POST aborts when the query key changes (e.g. user
 * expands a different field in the popover). Frees the HTTP/1.1 socket
 * slot so the latest request flies immediately instead of queueing
 * behind stale ones (PROD-3308).
 */
export const usePanelFilterOptions = (
  projectId: string | undefined,
  panel: ChartPanel | null,
  options?: { fields?: string[]; enabled?: boolean; faceted?: boolean },
): UseQueryResult<FilterOption[]> => {
  // bully ignore[no-service-hooks-outside-containers: sibling V1 filter-list hooks (use-filter-list, use-global-filter-list, use-local-filter-list, use-project-filter-list) all call useFilterService() at the hook level — the canonical query-hook shape in this codebase. Hoisting to the caller shifts the violation to no-cross-feature-private-imports on each caller's `features/filters/service` import without net architectural improvement (verified by experiment in PROD-2077 AC #10 cleanup). follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md.]
  const filterService = useFilterService()
  // Stable JSON keys for the parts of `panel` that affect counts. We don't
  // include the whole `panel` because UI-only fields (chartType, canvasMode,
  // etc.) shouldn't trigger a refetch. Same stringified-subkey pattern as
  // `use-chart-query.ts`.
  const filtersKey = useMemo(
    () => JSON.stringify(panel?.filters ?? []),
    [panel?.filters],
  )
  const dateKey = useMemo(
    () =>
      JSON.stringify({
        startDate: panel?.dateRange?.startDate,
        endDate: panel?.dateRange?.endDate,
      }),
    [panel?.dateRange?.startDate, panel?.dateRange?.endDate],
  )
  const searchTermsKey = useMemo(
    () => JSON.stringify(panel?.searchTerms ?? []),
    [panel?.searchTerms],
  )
  const searchCriteriaKey = useMemo(
    () => JSON.stringify(panel?.searchCriteria ?? {}),
    [panel?.searchCriteria],
  )
  const fieldsKey = useMemo(
    () => JSON.stringify(options?.fields ?? []),
    [options?.fields],
  )
  const faceted = options?.faceted ?? true
  // Under `enhanced_dialect` each control's counts come from a YogiQL query.
  const yogiql = useYogiQLFilterContext(projectId, filterService)

  const wanted =
    !!projectId &&
    !!panel &&
    (options?.fields?.length ?? 0) > 0 &&
    (options?.enabled ?? true)
  const query = useQuery(
    [
      'filter-list-data-v2',
      projectId,
      filtersKey,
      dateKey,
      searchTermsKey,
      searchCriteriaKey,
      fieldsKey,
      faceted,
      yogiql.mode,
    ],
    ({ signal }) =>
      fetchPanelFilterOptions(
        projectId ?? '',
        panel,
        filterService,
        {
          fields: options?.fields,
          faceted,
          yogiql: yogiql.mode === 'yogiql' ? yogiql : null,
        },
        signal,
      ),
    {
      refetchOnWindowFocus: false,
      enabled: wanted && yogiql.mode !== 'pending',
    },
  )
  // A disabled query reads as not loading; while the YogiQL context settles
  // the counts are still on their way, so say so.
  return wanted && yogiql.mode === 'pending'
    ? ({ ...query, isLoading: true } as UseQueryResult<FilterOption[]>)
    : query
}
